import { useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";

const field = "w-full border border-line bg-[#121212] px-4 py-3.5 text-white placeholder:text-zinc-500 outline-none transition-colors focus:border-cta";

export default function ReviewPage() {
  useSeo("Leave a review");
  const [f, setF] = useState({ name: "", email: "", role: "", product: "bundle", rating: 5, text: "", photo_url: "" });
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!api) return;
    setState("busy");
    setErr("");
    try {
      await api.post("/reviews", { ...f, role: f.role || null, photo_url: f.photo_url || null });
      setState("done");
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(Array.isArray(d) ? "Please check the fields. Your review needs at least 20 characters." : d || "Something went wrong.");
      setState("idle");
    }
  };

  return (
    <main data-testid="review-page" className="mx-auto max-w-3xl px-6 pb-24 pt-28 md:px-12 md:pt-36">
      <p className="eyebrow">Your honest review</p>
      <h1 className="font-display mt-5 text-4xl font-medium tracking-tight text-white md:text-6xl">How is it going?</h1>
      <p className="mt-5 text-zinc-300">Good or bad, your words help the next beginner decide. Reviews appear on the site after a quick check.</p>
      {state === "done" ? (
        <div data-testid="review-success" className="mt-12 border border-line p-8">
          <p className="font-display text-2xl text-white">Thank you. Your review was received.</p>
          <p className="mt-2 text-zinc-400">It will appear once it has been approved.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-12 grid gap-5" data-testid="review-form">
          <div className="grid gap-5 sm:grid-cols-2">
            <input required minLength={2} placeholder="Your name" value={f.name} onChange={set("name")} className={field} data-testid="review-name-input" />
            <input required type="email" placeholder="Email (not shown)" value={f.email} onChange={set("email")} className={field} data-testid="review-email-input" />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <input placeholder="Who you are, e.g. Nurse, starting out" value={f.role} onChange={set("role")} className={field} data-testid="review-role-input" />
            <select value={f.product} onChange={set("product")} className={field} data-testid="review-product-select">
              <option value="bundle">Bundle</option><option value="ebook">eBook</option><option value="workbook">Workbook</option>
            </select>
          </div>
          <div className="flex items-center gap-2" data-testid="review-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button type="button" key={n} onClick={() => setF({ ...f, rating: n })} aria-label={`${n} stars`} data-testid={`review-star-${n}`}>
                <Star size={26} className={n <= f.rating ? "fill-white text-white" : "text-zinc-600"} />
              </button>
            ))}
          </div>
          <textarea required minLength={20} rows={6} placeholder="What changed for you?" value={f.text} onChange={set("text")} className={field} data-testid="review-text-input" />
          <input type="url" placeholder="Photo link (optional)" value={f.photo_url} onChange={set("photo_url")} className={field} data-testid="review-photo-input" />
          {err && <p className="text-sm text-red-400" data-testid="review-error">{err}</p>}
          <button type="submit" disabled={state === "busy"} className="btn-cta justify-self-start" data-testid="review-submit-button">
            {state === "busy" ? <Loader2 size={18} className="animate-spin" /> : "Submit review"}
          </button>
        </form>
      )}
    </main>
  );
}
